import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useLMSLeadDialog } from '@/contexts/LMSLeadDialogContext';
import { Dialog, DialogContent, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const syncLeadToContacts = async (
  brandId: string,
  lead: { name?: string | null; email: string; phone?: string | null; activity?: string | null }
): Promise<string | null> => {
  if (!lead.email) return null;
  
  const nameParts = (lead.name || '').trim().split(/\s+/);
  const firstName = nameParts[0] || lead.email.split('@')[0];
  const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
  
  try {
    const { data: contactId, error } = await supabase.rpc('upsert_os_contact', {
      p_brand_id: brandId,
      p_email: lead.email,
      p_first_name: firstName,
      p_last_name: lastName,
      p_phone: lead.phone || null,
      p_module: ['lms'],
      p_activity: lead.activity ? [lead.activity] : ['lead'],
      p_organization: null,
      p_title: null,
      p_user_id: null,
    });
    
    if (error) {
      console.error('Error syncing lead to contacts:', error);
      return null;
    }
    
    return contactId;
  } catch (error) {
    console.error('Error syncing lead to contacts:', error);
    return null;
  }
};

interface Course {
  id: string;
  title: string;
  type: string;
}

export const GlobalLMSLeadDialog: React.FC = () => {
  const { currentBrandId } = useBrandManager();
  const queryClient = useQueryClient();
  const {
    isOpen,
    mode,
    lead,
    formData,
    closeDialog,
    updateFormData,
    onSuccess,
  } = useLMSLeadDialog();

  // Fetch courses for the dropdown
  const { data: courses = [] } = useQuery({
    queryKey: ['lms-courses-for-leads-global', currentBrandId],
    queryFn: async () => {
      if (!currentBrandId) return [];
      
      const { data, error } = await (supabase as any)
        .from('os_lms_courses')
        .select('id, title, type')
        .eq('brand_id', currentBrandId)
        .order('title');
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentBrandId && isOpen,
  });

  // Add lead mutation
  const addLeadMutation = useMutation({
    mutationFn: async () => {
      // Sync to contacts first
      const contactId = await syncLeadToContacts(currentBrandId!, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        activity: formData.activity,
      });

      const { error } = await (supabase as any)
        .from('os_lms_courses_leads')
        .insert({
          brand_id: currentBrandId,
          course_id: formData.course_id,
          email: formData.email,
          name: formData.name || null,
          phone: formData.phone || null,
          notes: formData.notes || null,
          activity: formData.activity || null,
          type: formData.type,
          status: formData.status,
          contact_id: contactId,
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lms-leads'] });
      toast.success('Lead added successfully');
      onSuccess?.();
      closeDialog();
    },
    onError: (error: any) => {
      toast.error(`Failed to add lead: ${error.message}`);
    },
  });

  // Update lead mutation
  const updateLeadMutation = useMutation({
    mutationFn: async () => {
      if (!lead) throw new Error('No lead selected');
      
      // Sync to contacts
      const contactId = await syncLeadToContacts(lead.brand_id, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        activity: formData.activity,
      });

      const { error } = await (supabase as any)
        .from('os_lms_courses_leads')
        .update({
          course_id: formData.course_id,
          email: formData.email,
          name: formData.name || null,
          phone: formData.phone || null,
          notes: formData.notes || null,
          activity: formData.activity || null,
          type: formData.type,
          status: formData.status,
          ...(contactId ? { contact_id: contactId } : {}),
        })
        .eq('id', lead.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lms-leads'] });
      toast.success('Lead updated successfully');
      onSuccess?.();
      closeDialog();
    },
    onError: (error: any) => {
      toast.error(`Failed to update lead: ${error.message}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !formData.course_id) {
      toast.error('Email and Course are required');
      return;
    }
    
    if (mode === 'add') {
      addLeadMutation.mutate();
    } else {
      updateLeadMutation.mutate();
    }
  };

  const isPending = addLeadMutation.isPending || updateLeadMutation.isPending;
  const isAddMode = mode === 'add';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogTitleWithBug
          popupName={isAddMode ? 'Add New Lead' : 'Edit Lead'}
          popupContext="/lms/leads"
          isOpen={isOpen}
        >
          {isAddMode ? 'Add New Lead' : 'Edit Lead'}
        </DialogTitleWithBug>
        <DialogDescription>
          {isAddMode 
            ? 'Manually add a lead to a course waiting list or pre-registration.'
            : 'Update lead information and status.'}
        </DialogDescription>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="course">Course *</Label>
            <Select 
              value={formData.course_id} 
              onValueChange={(value) => updateFormData({ course_id: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a course" />
              </SelectTrigger>
              <SelectContent>
                {courses.map((course: Course) => (
                  <SelectItem key={course.id} value={course.id}>
                    {course.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => updateFormData({ email: e.target.value })}
              placeholder="lead@example.com"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => updateFormData({ name: e.target.value })}
              placeholder="John Doe"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => updateFormData({ phone: e.target.value })}
              placeholder="+1234567890"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select 
                value={formData.type} 
                onValueChange={(value) => updateFormData({ type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="waiting_list">Waiting List</SelectItem>
                  <SelectItem value="pre_registration">Pre-Registration</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select 
                value={formData.status} 
                onValueChange={(value) => updateFormData({ status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => updateFormData({ notes: e.target.value })}
              placeholder="Additional notes about this lead..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="activity">Activity</Label>
            <Input
              id="activity"
              value={formData.activity}
              onChange={(e) => updateFormData({ activity: e.target.value })}
              placeholder="Activity identifier (optional)"
            />
          </div>
          
          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending 
                ? (isAddMode ? 'Adding...' : 'Saving...') 
                : (isAddMode ? 'Add Lead' : 'Save Changes')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
